/**
 * Actions Tab — Conversion Engine
 * Decision interface: browse → compare → select → generate pack → financing
 * Data: /api/actions/assessments/{id}
 * No duplicate calculations. All values from Action Intelligence Engine.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  ArrowLeft, Package, Zap, TrendingDown, Clock, DollarSign,
  ChevronDown, ChevronRight, Check, Circle, RefreshCw,
  AlertTriangle, Sparkles, Info, BarChart2
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────
const ACTION_META = {
  solar_pv:              { label: "Solar PV",             color: "bg-yellow-50 border-yellow-200",  badge: "bg-yellow-100 text-yellow-800" },
  energy_efficiency:     { label: "Energy Efficiency",    color: "bg-blue-50 border-blue-200",      badge: "bg-blue-100 text-blue-800" },
  fuel_switch:           { label: "Fuel Switch",          color: "bg-orange-50 border-orange-200",  badge: "bg-orange-100 text-orange-800" },
  fleet_electrification: { label: "Fleet Electrification",color: "bg-violet-50 border-violet-200",  badge: "bg-violet-100 text-violet-800" },
  waste_reduction:       { label: "Waste Reduction",      color: "bg-teal-50 border-teal-200",      badge: "bg-teal-100 text-teal-800" },
  materials_optimization:{ label: "Low-Carbon Materials", color: "bg-green-50 border-green-200",    badge: "bg-green-100 text-green-800" },
};
const DEFAULT_META = { label: "Action", color: "bg-slate-50 border-slate-200", badge: "bg-slate-100 text-slate-700" };

const CONF_STYLE = {
  high:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low:    "bg-red-50 text-red-700 border-red-200",
};

const SORT_OPTIONS = [
  { key: "impact",  label: "Highest Impact",  fn: (a,b) => (b.priority_score||0)   - (a.priority_score||0)   },
  { key: "payback", label: "Fastest Payback",  fn: (a,b) => (a.payback_years||99)   - (b.payback_years||99)   },
  { key: "capex",   label: "Lowest CapEx",     fn: (a,b) => (a.capex_required||0)   - (b.capex_required||0)   },
];

const fmt    = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";
const fmtK   = (n) => !n ? "$0" : n>=1e6 ? `$${(n/1e6).toFixed(1)}M` : n>=1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n)}`;
const fmtNum = (n) => n != null && !isNaN(n) ? (+n).toLocaleString(undefined, {maximumFractionDigits:0}) : "—";

// ── Action Card ────────────────────────────────────────────────────────────
function ActionCard({ action, selected, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const meta     = ACTION_META[action.action_type] || DEFAULT_META;
  const isSelected = selected;
  const aid      = action.action_id || action.id;
  const confStyle= CONF_STYLE[action.confidence_level] || CONF_STYLE.medium;

  // Parse assumption from derivation_notes
  const rawNotes   = action.derivation_notes || "";
  const displayNotes = rawNotes.split("Assumptions:")[0].trim() || rawNotes;

  const metrics = [
    { icon: TrendingDown, label: "CO₂/yr",      value: `${fmt(action.co2_reduction, 1)} t`,  color: "text-[#059669]" },
    { icon: DollarSign,   label: "Annual Savings",value: fmtK(action.annual_savings),          color: "text-blue-600"  },
    { icon: DollarSign,   label: "CapEx",         value: fmtK(action.capex_required),          color: "text-slate-900" },
    { icon: Clock,        label: "Payback",        value: `${fmt(action.payback_years, 1)} yrs`,color: "text-amber-600" },
    { icon: Zap,          label: "ROI",            value: `${fmt(action.roi_pct, 0)}%`,         color: "text-indigo-600"},
    { icon: BarChart2,    label: "Priority",       value: `${fmt(action.priority_score, 0)}/100`,color:"text-slate-600"  },
  ];

  return (
    <div
      data-testid={`action-card-${action.action_type}`}
      className={`rounded-xl border-2 p-5 transition-all duration-200 cursor-pointer
        ${isSelected
          ? "border-[#059669] bg-emerald-50/30 shadow-md shadow-emerald-100"
          : `${meta.color} hover:shadow-sm hover:-translate-y-0.5`}`}
      onClick={() => onToggle(aid)}
    >
      {/* Card header */}
      <div className="flex items-start justify-between mb-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <button
            onClick={() => onToggle(aid)}
            data-testid={`action-select-${action.action_type}`}
            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all
              ${isSelected ? "bg-[#059669] border-[#059669]" : "border-slate-300 bg-white hover:border-[#059669]"}`}
          >
            {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-slate-900">{meta.label}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${confStyle}`}>
                {action.confidence_level} confidence
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{action.description}</p>
          </div>
        </div>
        <span className="text-xs font-bold text-slate-400 flex-shrink-0 ml-2 tabular-nums">
          {fmt(action.priority_score, 0)}<span className="font-normal">/100</span>
        </span>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {metrics.map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white/70 rounded-xl p-2.5 text-center border border-white">
            <p className={`text-sm font-bold tabular-nums ${color}`}>{value}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wide mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Expandable derivation */}
      <div onClick={e => e.stopPropagation()}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          data-testid={`action-expand-${action.action_type}`}
        >
          {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          {expanded ? "Hide" : "Show"} assumption detail
        </button>
        {expanded && displayNotes && (
          <div className="mt-2 p-3 bg-white/60 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 leading-relaxed">{displayNotes}</p>
            <p className="text-xs text-slate-400 mt-1.5 italic">
              Sources: IRENA 2023 · IEA 2024 · SEAI/UNEP · Ellen MacArthur Foundation
            </p>
          </div>
        )}
      </div>

      {/* Per-card CTA */}
      <div className="mt-3 pt-3 border-t border-white/60 flex items-center justify-between" onClick={e => e.stopPropagation()}>
        <button
          onClick={() => onToggle(aid)}
          data-testid={`action-add-btn-${action.action_type}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all
            ${isSelected
              ? "bg-[#059669] text-white hover:bg-emerald-700"
              : "bg-white border border-slate-300 text-slate-600 hover:border-[#059669] hover:text-[#059669]"}`}
        >
          {isSelected ? <><Check size={11} /> Added to Pack</> : <><Package size={11} /> Add to Pack</>}
        </button>
        {isSelected && <span className="text-xs text-[#059669] font-medium">Included in investment pack</span>}
      </div>
    </div>
  );
}

// ── Selection Panel ────────────────────────────────────────────────────────
function SelectionPanel({ selected, actions, onGenerate, generating, assessmentId }) {
  const selectedActions = actions.filter(a => selected.has(a.action_id || a.id));
  const totalCapex   = selectedActions.reduce((s,a) => s + (a.capex_required || 0), 0);
  const totalSavings = selectedActions.reduce((s,a) => s + (a.annual_savings  || 0), 0);
  const totalCO2     = selectedActions.reduce((s,a) => s + (a.co2_reduction   || 0), 0);
  const payback      = totalSavings > 0 ? totalCapex / totalSavings : 0;
  const hasSelection = selectedActions.length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="selection-panel">
      {/* Panel header */}
      <div className="bg-[#0F172A] px-5 py-4">
        <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Your Selection</p>
        <p className="text-white font-bold text-base">
          {selectedActions.length} action{selectedActions.length !== 1 ? "s" : ""} selected
        </p>
      </div>

      <div className="p-5 space-y-4">
        {/* Selected list */}
        {hasSelection ? (
          <div className="space-y-1.5" data-testid="selected-list">
            {selectedActions.map(a => {
              const meta = ACTION_META[a.action_type] || DEFAULT_META;
              return (
                <div key={a.action_id || a.id} className="flex items-center gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#059669] flex-shrink-0" />
                  <span className="text-slate-700 font-medium">{meta.label}</span>
                  <span className="text-slate-400 ml-auto tabular-nums">{fmt(a.priority_score,0)}/100</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <Package size={24} className="mx-auto text-slate-200 mb-2" />
            <p className="text-xs text-slate-400">Select actions above to build your investment pack</p>
          </div>
        )}

        {/* Aggregate metrics */}
        {hasSelection && (
          <>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Aggregated Metrics</p>
              <div className="space-y-2.5">
                {[
                  { label: "Total CapEx",      value: fmtK(totalCapex),       color: "text-slate-900", testid: "total-capex"   },
                  { label: "Annual Savings",   value: `${fmtK(totalSavings)}/yr`,color:"text-[#059669]",testid: "total-savings" },
                  { label: "CO₂ Reduction",    value: `${fmt(totalCO2,1)} t/yr`,color:"text-blue-600",  testid: "total-co2"    },
                  { label: "Payback Period",   value: payback > 0 ? `${fmt(payback,1)} yrs` : "—", color:"text-amber-600", testid:"panel-payback" },
                ].map(({label, value, color, testid}) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{label}</span>
                    <span className={`text-sm font-bold tabular-nums ${color}`} data-testid={testid}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Carbon value hint */}
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <p className="text-xs text-blue-700">
                <strong>Carbon value potential:</strong> {fmtK(totalCO2 * 5)}–{fmtK(totalCO2 * 15)}/yr
                <span className="font-normal"> at $5–15/tCO₂e</span>
              </p>
            </div>
          </>
        )}

        {/* CTA */}
        <button
          onClick={onGenerate}
          disabled={!hasSelection || generating}
          data-testid="generate-pack-btn"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {generating
            ? <><RefreshCw size={14} className="animate-spin" /> Generating...</>
            : <><Package size={14} /> Generate Investment Pack</>}
        </button>

        {/* Disclaimer */}
        <div className="flex items-start gap-2 text-xs text-slate-400">
          <Info size={11} className="mt-0.5 flex-shrink-0" />
          <p>All estimates rule-based. No AI for core calculations. Sources: IRENA, IEA, UNEP.</p>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function ActionsPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [actions,   setActions]   = useState([]);
  const [selected,  setSelected]  = useState(new Set());
  const [sortKey,   setSortKey]   = useState("impact");
  const [loading,   setLoading]   = useState(true);
  const [generating,setGenerating]= useState(false);
  const [assessment, setAssessment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, actRes] = await Promise.allSettled([
        client.get(`/assessments/${id}`),
        client.get(`/actions/assessments/${id}`),
      ]);
      if (aRes.status === "fulfilled") setAssessment(aRes.value.data);

      let acts = aRes.status === "fulfilled" && actRes.status === "fulfilled"
        ? actRes.value.data?.actions || []
        : [];

      // Auto-generate if empty (assessment has calculations)
      if (acts.length === 0) {
        try {
          const gen = await client.post(`/actions/assessments/${id}/generate`);
          acts = gen.data.actions || [];
          if (acts.length > 0) toast.success(`${acts.length} action recommendations generated.`);
        } catch {}
      }

      setActions(acts);
      // Pre-select top 2 by priority
      const sorted = [...acts].sort((a,b) => (b.priority_score||0) - (a.priority_score||0));
      setSelected(new Set(sorted.slice(0, Math.min(2, acts.length)).map(a => a.action_id || a.id)));
    } catch { toast.error("Failed to load actions"); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleAction = useCallback((actionId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(actionId) ? next.delete(actionId) : next.add(actionId);
      return next;
    });
  }, []);

  const handleGenerate = useCallback(async () => {
    if (selected.size === 0) { toast.warning("Select at least one action first."); return; }
    setGenerating(true);
    try {
      const actionIds = Array.from(selected).join(",");
      await client.post(`/packs/assessments/${id}/generate?action_ids=${encodeURIComponent(actionIds)}`);
      toast.success("Investment Pack generated!");
      navigate(`/assessments/${id}?tab=investment-pack`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pack generation failed. Ensure calculations are run.");
    }
    setGenerating(false);
  }, [id, selected, navigate]);

  const handleRegenerate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.post(`/actions/assessments/${id}/generate`);
      const acts = res.data.actions || [];
      setActions(acts);
      const sorted = [...acts].sort((a,b) => (b.priority_score||0) - (a.priority_score||0));
      setSelected(new Set(sorted.slice(0, Math.min(2, acts.length)).map(a => a.action_id || a.id)));
      toast.success(`${acts.length} actions regenerated.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Regeneration failed"); }
    setLoading(false);
  }, [id]);

  const sortedActions = [...actions]
    .sort(SORT_OPTIONS.find(o => o.key === sortKey).fn)
    .slice(0, 6);

  if (loading) return (
    <div className="p-6 lg:p-8 space-y-4">
      <div className="h-8 w-56 bg-slate-200 rounded animate-pulse" />
      <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-64 bg-slate-100 rounded-xl animate-pulse" />)}</div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="actions-page">

      {/* ── Section A: Header ── */}
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 mb-4 transition-colors">
          <ArrowLeft size={13} /> Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <PageHeader title="Action Plan" />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Available Actions" value={actions.length} color="text-slate-900" />
      </div>
              {actions.length > 0 && (
                <span className="text-sm px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                  {actions.length} recommendation{actions.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">
              {assessment?.title ? `${assessment.title} · ` : ""}
              Compare, select, and generate your investment pack.
            </p>
          </div>
          <button onClick={handleRegenerate} disabled={loading}
            data-testid="regenerate-btn"
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-slate-600 text-xs font-medium rounded-xl hover:bg-slate-50 transition-colors">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Regenerate
          </button>
        </div>
      </div>

      {/* No actions state */}
      {actions.length === 0 && (
        <SectionCard>

          <AlertTriangle size={32} className="mx-auto text-amber-400 mb-3" />
          <h3 className="text-sm font-semibold text-slate-900 mb-1">No actions available</h3>
          <p className="text-xs text-slate-500 mb-4 max-w-sm mx-auto">
            Actions require emission calculations to be run first. Open the assessment workspace and run calculations.
          </p>
          <button onClick={() => navigate(`/assessments/${id}`)}
            className="px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-xl">
            Open Assessment
          </button>
        
        </SectionCard>
      )}

      {actions.length > 0 && (
        <div className="flex gap-6 items-start">

          {/* ── Left: Cards ── */}
          <div className="flex-1 min-w-0">

            {/* Section B: Filter Toggle */}
            <div className="flex items-center gap-3 mb-5" data-testid="filter-strip">
              <p className="text-xs text-slate-400 font-medium">Sort by:</p>
              <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.key} onClick={() => setSortKey(opt.key)}
                    data-testid={`sort-${opt.key}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all
                      ${sortKey === opt.key
                        ? "bg-[#0F172A] text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-slate-400 ml-auto">
                {selected.size} of {sortedActions.length} selected
              </span>
            </div>

            {/* Section C: Action Cards */}
            <div className="space-y-4">
              {sortedActions.map(action => (
                <ActionCard
                  key={action.action_id || action.id}
                  action={action}
                  selected={selected.has(action.action_id || action.id)}
                  onToggle={toggleAction}
                />
              ))}
            </div>

            {/* Methodology note */}
            <div className="mt-5 flex items-start gap-2 p-4 bg-white border border-slate-200 rounded-xl">
              <Info size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-400 leading-relaxed">
                <strong className="text-slate-500">Methodology:</strong> All financial estimates are rule-based and deterministic. 
                Reference ranges use midpoint defaults. No AI for core calculations. 
                Capex/savings vary by geography, sector, and organization type.
              </p>
            </div>
          </div>

          {/* ── Right: Selection Panel (sticky) ── */}
          <div className="w-72 flex-shrink-0 hidden lg:block">
            <div className="sticky top-6">
              <SelectionPanel
                selected={selected}
                actions={actions}
                onGenerate={handleGenerate}
                generating={generating}
                assessmentId={id}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile CTA (visible below lg) */}
      {actions.length > 0 && selected.size > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-40">
          <button onClick={handleGenerate} disabled={generating}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#059669] text-white text-sm font-semibold rounded-xl disabled:opacity-50">
            {generating ? <RefreshCw size={14} className="animate-spin" /> : <Package size={14} />}
            Generate Pack ({selected.size} action{selected.size!==1?"s":""})
          </button>
        </div>
      )}
    </div>
  );
}
